import React, { useState, useEffect, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../AppContext';

const LocationIpGuard = ({ onPass, onRefuse }) => {
    const { appFunctions, currentUserProfile, updateUserProfile } = useAppContext();
    const [step, setStep] = useState('initial'); // initial, checking, prompt, denied, success
    const [status, setStatus] = useState({ ip: 'pending', location: 'pending', profile: 'pending' });
    const [errorMsg, setErrorMsg] = useState('');
    const [checkStage, setCheckStage] = useState('Initializing...');

    // Failsafe: Force exit if stuck in checking for too long
    useEffect(() => {
        let timer;
        if (step === 'checking') {
            timer = setTimeout(() => {
                setStep('denied');
                setErrorMsg("Verification timed out. Please check your connection.");
            }, 15000); 
        }
        return () => clearTimeout(timer);
    }, [step]);

    const runChecks = useCallback(async () => {
        // setStep('checking') handled in handleGrant
        setErrorMsg('');
        setCheckStage('Verifying Connection Identity...');

        const withTimeout = (promise, ms = 5000) => {
            return Promise.race([
                promise,
                new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms))
            ]);
        };
        
        let ipSuccess = false;
        let locSuccess = false;
        let profileSuccess = false;

        // 1. IP Check & Creation IP Sync
        let detectedIp = null;

        try {
            // Attempt 1: External API
            const response = await withTimeout(fetch('https://api.ipify.org?format=json'), 4000);
            if (response.ok) {
                const data = await response.json();
                detectedIp = data.ip;
            }
        } catch (e) {
            console.warn("Primary IP Check (ipify) Failed, attempting fallback...", e);
        }

        // Attempt 2: Internal Cloud Function Fallback
        if (!detectedIp) {
            try {
                const getUserIpFn = httpsCallable(appFunctions, 'getUserIp');
                const result = await withTimeout(getUserIpFn(), 5000);
                detectedIp = result.data.ip;
            } catch (e) {
                console.error("Secondary IP Check (Cloud Function) Failed:", e);
            }
        }

        if (detectedIp) {
            setCheckStage('Synchronizing Connection Data...');
            // Strict Check: Is creationIp recorded and a string?
            if (currentUserProfile && currentUserProfile.creationIp && typeof currentUserProfile.creationIp === 'string') {
                setStatus(prev => ({ ...prev, ip: 'valid' }));
                ipSuccess = true;
            } else {
                // Attempt to backfill if missing or not a string
                try {
                    // Use the client-side updateUserProfile function to set creationIp
                    await updateUserProfile(currentUserProfile.id, { creationIp: detectedIp });
                    setStatus(prev => ({ ...prev, ip: 'valid' })); // Optimistically mark as valid
                    ipSuccess = true;
                } catch (err) {
                    console.error("IP Sync Failed:", err);
                    setStatus(prev => ({ ...prev, ip: 'error' }));
                    setErrorMsg("Failed to secure connection identity.");
                }
            }
        } else {
            // Both methods failed
            setStatus(prev => ({ ...prev, ip: 'error' }));
            setErrorMsg("Network error: Unable to verify IP address.");
        }

        // 2. Location Check & Profile Sync
        setCheckStage('Requesting Location Access...');
        if ("geolocation" in navigator) {
            try {
                const position = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        timeout: 10000,
                        maximumAge: 0,
                        enableHighAccuracy: true
                    });
                });
                
                if (position && position.coords) {
                    setStatus(prev => ({ ...prev, location: 'granted' }));
                    locSuccess = true;

                    // 3. Profile Data Integrity Check
                    const { latitude, longitude } = position.coords;
                    const profileLoc = currentUserProfile?.location;
                    
                    // Check if valid location data exists in the profile
                    const isLocationSynced = profileLoc && 
                                             typeof profileLoc.geohash === 'string' && 
                                             typeof profileLoc.latitude === 'number' && 
                                             typeof profileLoc.longitude === 'number';

                    if (isLocationSynced) {
                        setStatus(prev => ({ ...prev, profile: 'valid' }));
                        profileSuccess = true;
                    } else {
                        // Data missing or incomplete, attempt to sync
                        try {
                            const updateUserLocation = httpsCallable(appFunctions, 'updateUserLocation');
                            await updateUserLocation({ latitude, longitude });
                            setStatus(prev => ({ ...prev, profile: 'valid' }));
                            profileSuccess = true;
                        } catch (err) {
                            console.error("Profile Sync Failed:", err);
                            setStatus(prev => ({ ...prev, profile: 'error' }));
                            setErrorMsg("Failed to sync location to profile.");
                        }
                    }
                }
            } catch (error) {
                console.error("Location Check Failed:", error);
                setStatus(prev => ({ ...prev, location: 'denied' }));
                if (error.code === error.PERMISSION_DENIED) {
                    setErrorMsg("Location permission was denied.");
                } else if (error.code === error.TIMEOUT) {
                    setErrorMsg("Location request timed out.");
                } else {
                    setErrorMsg("Location unavailable.");
                }
            }
        }

        if (ipSuccess && locSuccess && profileSuccess) {
            setStep('success');
            setTimeout(onPass, 1500); // Brief success state before proceeding
        } else if (step !== 'initial') { // Only go to denied screen if checks were actively run
            setStep('denied'); // Redirect to denied/retry screen on failure
        }
    }, [onPass, appFunctions, currentUserProfile, updateUserProfile]);

    const handleGrant = () => {
        setStep('checking');
        setCheckStage('Preparing verification...');
        setTimeout(() => runChecks(), 100);
    };

    if (step === 'initial' || step === 'prompt') {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto animate-fadeIn">
                <div className="mb-6 relative">
                    <div className="absolute inset-0 bg-blue-500 blur-xl opacity-20 rounded-full"></div>
                    <LucideIcons.ShieldCheck size={64} className="text-blue-400 relative z-10" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4 font-playfair">Secure Gateway</h2>
                <p className="text-gray-300 mb-6 leading-relaxed">
                    To ensure a safe and authentic experience for our community, we verify the 
                    <strong> Location</strong> and <strong>Connection Integrity</strong> of all travelers.
                </p>
                
                <div className="flex flex-col gap-3 w-full mb-8 text-left bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                    <div className="flex items-center gap-3">
                        {status.location === 'granted' ? <LucideIcons.CheckCircle2 className="text-green-400" size={20} /> : <LucideIcons.MapPin className="text-gray-400" size={20} />}
                        <span className={status.location === 'granted' ? 'text-green-100' : 'text-gray-300'}>Location Services</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {status.profile === 'valid' ? <LucideIcons.CheckCircle2 className="text-green-400" size={20} /> : <LucideIcons.Database className="text-gray-400" size={20} />}
                        <span className={status.profile === 'valid' ? 'text-green-100' : 'text-gray-300'}>Profile Alignment</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {status.ip === 'valid' ? <LucideIcons.CheckCircle2 className="text-green-400" size={20} /> : <LucideIcons.Network className="text-gray-400" size={20} />}
                        <span className={status.ip === 'valid' ? 'text-green-100' : 'text-gray-300'}>Connection Validation</span>
                    </div>
                </div>

                {errorMsg && <p className="text-red-400 text-sm mb-4">{errorMsg}</p>}

                <div className="flex gap-4 w-full">
                    <button 
                        onClick={onRefuse}
                        className="flex-1 px-6 py-3 rounded-full border border-gray-600 text-gray-400 hover:bg-gray-800 hover:text-white transition-all"
                    >
                        Leave
                    </button>
                    <button 
                        onClick={handleGrant}
                        className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold shadow-lg shadow-blue-900/30 hover:shadow-blue-900/50 hover:scale-105 transition-all"
                    >
                        Enter Harmony
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-6">
                    Your location data is used solely for local discovery features and security. We never sell your data.
                </p>
            </div>
        );
    }

    if (step === 'checking') {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center">
                <div className="w-16 h-16 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-6"></div>
                <h3 className="text-xl font-bold text-white mb-2">Verifying Connection...</h3>
                <p className="text-gray-400">{checkStage}</p>
            </div>
        );
    }

    if (step === 'denied') {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto animate-fadeIn">
                <div className="mb-6 relative">
                    <div className="absolute inset-0 bg-red-500 blur-xl opacity-20 rounded-full"></div>
                    <LucideIcons.AlertTriangle size={64} className="text-red-400 relative z-10" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4 font-playfair">Access Restricted</h2>
                <p className="text-gray-300 mb-6 leading-relaxed">
                    We understand your hesitation, but Whispers of Harmony is built on a foundation of <strong>verified, authentic connections</strong>. Without location validation, we cannot guarantee the safety of our local communities.
                </p>
                <p className="text-gray-300 mb-8 leading-relaxed italic">
                    "Trust is the currency of our cosmos. Please reconsider helping us keep this space secure for everyone."
                </p>

                <div className="flex gap-4 w-full">
                    <button 
                        onClick={onRefuse}
                        className="flex-1 px-6 py-3 rounded-full border border-gray-600 text-gray-400 hover:bg-gray-800 hover:text-white transition-all"
                    >
                        I Understand
                    </button>
                    <button 
                        onClick={handleGrant}
                        className="flex-1 px-6 py-3 rounded-full bg-gradient-to-r from-green-600 to-teal-600 text-white font-bold shadow-lg shadow-green-900/30 hover:shadow-green-900/50 hover:scale-105 transition-all"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center animate-fadeIn">
                <LucideIcons.Unlock size={64} className="text-green-400 mb-6 animate-bounce" />
                <h3 className="text-xl font-bold text-white mb-2">Access Granted</h3>
                <p className="text-gray-400">Welcome to Whispers of Harmony.</p>
            </div>
        );
    }

    return null;
};

export default LocationIpGuard;
